import { SelectOptionsList } from '@/lib/Selects/Select/SelectOptionsList';
import { SelectOption } from '@/lib/Selects/Select/types';
import { PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { Listbox } from '@headlessui/react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { getFilterOptionDisplayLabel, toFilterMenuOptions } from '../../utils/filterOptions';

type FilterPillSelectProps = {
  icon: ReactNode;
  categoryLabel?: string;
  selected: PerformanceAudienceFilterOption | null;
  items: PerformanceAudienceFilterOption[];
  onChange: (option: SelectOption) => void;
  variant?: 'labeled' | 'date';
  disabled?: boolean;
  className?: string;
};

export const FilterPillSelect: FC<FilterPillSelectProps> = ({
  icon,
  categoryLabel,
  selected,
  items,
  onChange,
  variant = 'labeled',
  disabled,
  className,
}) => {
  const { t } = useTranslation('performanceAudience');
  const menuItems = toFilterMenuOptions(items, t);
  const selectedMenuItem = menuItems.find((item) => item.id === selected?.id) ?? null;
  const displayLabel = selected ? getFilterOptionDisplayLabel(selected, t) : '';

  return (
    <Listbox value={selectedMenuItem} onChange={onChange} disabled={disabled} by={(a, b) => a?.id === b?.id}>
      {({ open }) => (
        <div className={twMerge('relative min-w-0', className)}>
          <Listbox.Button
            className={twMerge(
              'flex w-full items-center gap-2.5 rounded-xl border border-primary-border bg-white px-3.5 py-2.5 text-left shadow-sm',
              'hover:border-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              'motion-safe:transition-colors motion-safe:duration-200 motion-reduce:transition-none',
              open && 'border-brand-400 ring-2 ring-brand-500/20'
            )}
          >
            <span className='shrink-0'>{icon}</span>
            <span className='min-w-0 flex-1 truncate'>
              {variant === 'labeled' && categoryLabel ? (
                <span className='text-sm text-tertiary-600'>
                  {categoryLabel}{' '}
                  <span className='font-semibold text-primary-900'>{displayLabel}</span>
                </span>
              ) : (
                <span className='text-sm font-semibold text-primary-900'>{displayLabel}</span>
              )}
            </span>
            <ChevronDownIcon className='size-4 shrink-0 text-quaternary-400' aria-hidden />
          </Listbox.Button>
          <SelectOptionsList
            open={open}
            items={menuItems}
            classNames={{
              wrapper: 'min-w-[16rem]',
            }}
          />
        </div>
      )}
    </Listbox>
  );
};
